export type PipelineSourceType = 'PROJECT' | 'LANDSALE' | 'CUSTOM';
export type DeckRenderMode = 'interactive' | 'print';
export type PipelineMarketRegion = 'CCR' | 'RCR' | 'OCR';
export type PipelineCandidateGroup = 'UPCOMING' | 'REDEVELOPMENT' | 'ENBLOC' | 'GLS';

export type CardFieldKey =
  | 'projectName' | 'roadName' | 'status' | 'targetPreview' | 'district' | 'tenure'
  | 'estUnits' | 'developer' | 'landCost' | 'estLaunchPrice';

export interface CardFieldStyle {
  visible?: boolean;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  highlight?: string;
  /** Logical canvas font size in pixels. */
  fontSize?: number;
}

export interface CardDisplaySettings {
  fields?: Partial<Record<CardFieldKey, CardFieldStyle>>;
}

export type PipelineSection =
  | 'NORTH'
  | 'NORTH_EAST'
  | 'CENTRAL'
  | 'EAST'
  | 'WEST'
  | 'EC'
  | 'COMMERCIAL'
  | 'INDUSTRIAL'
  | 'COMMERCIAL_INDUSTRIAL';

/** Saved deck entry (TransformationPipelineItem). */
export interface PipelineItem {
  id: number;
  sourceType: PipelineSourceType;
  projectId: string | null;
  landSaleId: number | null;
  projectName: string | null;
  roadName: string | null;
  featuredImage?: string | null;
  remarks: string | null;
  status: string | null;
  section: PipelineSection;
  district: string | null;
  tenure: string | null;
  tenureId?: string | number | null;
  estUnits: string | null;
  developer: string | null;
  landCost: string | null;
  estLaunchPrice: string | null;
  targetPreview: string | null;
  isLaunchingSoon: boolean;
  hasProjectName: boolean;
  landAwarded: boolean;
  previewConfirmed: boolean;
  latitude: number | null;
  longitude: number | null;
  region: string | null;
  /** Authoritative market-region code from Project.Area or ProjectLandSale.Region. */
  marketRegion: PipelineMarketRegion | null;
  sortOrder: number;
}

/** Candidate row offered on the Input tab (not yet saved). */
export interface PipelineCandidate extends Omit<PipelineItem, 'id' | 'sortOrder' | 'remarks'> {
  key: string;
  existingItemId: number | null;
  candidateGroup: PipelineCandidateGroup;
  completeDate?: string | null;
  availableUnits?: number | null;
  recentSoldCount?: number | null;
  tagScale?: number;
  tagRotation?: number;
  tagX?: number;
  tagY?: number;
}

/** Draft = candidate + user edits, keyed by candidate key. */
export interface PipelineDraft extends Omit<PipelineItem, 'id' | 'sortOrder'> {
  key: string;
  /** Database row identity used by generation upserts. */
  itemId: number | null;
}

export interface CardPosition {
  /** Internal marker used to migrate legacy downstream card geometry once. */
  placementVersion?: number;
  x?: number; // % of canvas width (card top-left)
  y?: number; // % of canvas height
  /** Optional element width, as a percentage of the canvas width. */
  width?: number;
  /** Optional element height, as a percentage of the canvas height. */
  height?: number;
  /** Optional table-column widths, stored as percentages of the table width. */
  columnWidths?: Record<string, number>;
  /** Whether resizing should preserve the element's current aspect ratio. */
  aspectLocked?: boolean;
  /** Optional leader-line bend point, also in canvas percentages. */
  bendX?: number;
  bendY?: number;
  /** Optional second leader-line bend point. */
  bend2X?: number;
  bend2Y?: number;
  /** Optional leader-line bend points for long routes. */
  bend3X?: number;
  bend3Y?: number;
  bend4X?: number;
  bend4Y?: number;
  bend5X?: number;
  bend5Y?: number;
  bend6X?: number;
  bend6Y?: number;
  bend7X?: number;
  bend7Y?: number;
  /** Leader-line routing and card attachment configuration. */
  /** Whether the connector is rendered. Missing values default to visible. */
  connectorVisible?: boolean;
  /** Page-level connector stroke width in logical canvas pixels. */
  connectorStrokeWidth?: number;
  lineStyle?: 'straight' | 'one-bend' | 'two-bend' | 'three-bend' | 'four-bend' | 'five-bend' | 'six-bend' | 'seven-bend';
  anchorSide?: 'top' | 'right' | 'bottom' | 'left';
  anchorOffset?: number; // 0..100 along the selected card edge
  /** Shared-marker fork junction, stored in canvas percentages. */
  forkX?: number;
  forkY?: number;
  /** Optional bends on the shared marker-to-junction segment. */
  /** True after the user explicitly edits the shared marker-side route. */
  markerRouteEdited?: boolean;
  markerLineStyle?: CardPosition['lineStyle'];
  markerBendX?: number;
  markerBendY?: number;
  markerBend2X?: number;
  markerBend2Y?: number;
  markerBend3X?: number;
  markerBend3Y?: number;
  markerBend4X?: number;
  markerBend4Y?: number;
  markerBend5X?: number;
  markerBend5Y?: number;
  markerBend6X?: number;
  markerBend6Y?: number;
  markerBend7X?: number;
  markerBend7Y?: number;
  /** Optional colour override used by special per-region layout entries. */
  color?: string;
  /** Optional launching-soon legend/card colour override. */
  launchingSoonColor?: string;
  /** Optional launching-soon legend label override. */
  launchingSoonText?: string;
  /** User-uploaded page background stored on the __background layout entry. */
  backgroundImage?: string;
  /** Shared card presentation settings for one downstream tab. */
  cardDisplay?: CardDisplaySettings;
}

export type PageLayout = Record<string, CardPosition>;

export interface PipelineSavedDraft {
  version: 1;
  overviewItems: PipelineItem[];
  downstreamAdditions: PipelineItem[];
  layouts: Record<string, PageLayout>;
  /** Per-page item edits that must not mutate Overview or source data. */
  pageItemOverrides?: Record<string, Record<string, Partial<PipelineItem>>>;
}

export interface PipelineDraftRevision {
  id: number;
  createdAt: string | null;
  updatedAt: string | null;
  defunctAt: string | null;
  updatedByName: string | null;
  draft: PipelineSavedDraft;
}

export type PageKey =
  | 'cover'
  | 'overview'
  | 'residential'
  | 'commercial-industrial'
  | 'north-north-east'
  | 'central'
  | 'east'
  | 'west'
  | 'ec';

export interface PageDef {
  key: PageKey;
  label: string;
  /** Sub-heading shown under "PROJECTS IN THE PIPELINE" on the page. */
  heading: string | null;
  /** Accent heading (e.g. "NORTH & NORTH—EAST") shown below the sub-heading. */
  accent: string | null;
  accentColor: string;
  sections: PipelineSection[] | null; // null = no cards page (cover / overview)
}

export const SECTION_ORDER: PipelineSection[] = [
  'NORTH', 'NORTH_EAST', 'CENTRAL', 'WEST', 'EAST', 'EC', 'COMMERCIAL', 'INDUSTRIAL', 'COMMERCIAL_INDUSTRIAL',
];

export const SECTION_LABELS: Record<PipelineSection, string> = {
  NORTH: 'NORTH',
  NORTH_EAST: 'NORTH—EAST',
  CENTRAL: 'CENTRAL',
  EAST: 'EAST',
  WEST: 'WEST',
  EC: 'EXECUTIVE CONDO (EC)',
  COMMERCIAL: 'COMMERCIAL',
  INDUSTRIAL: 'INDUSTRIAL',
  COMMERCIAL_INDUSTRIAL: 'COMMERCIAL & INDUSTRIAL',
};

/** Deck card header colours (matched to the reference deck). */
export const SECTION_COLORS: Record<PipelineSection, { bg: string; light: string; fg: string }> = {
  NORTH: { bg: '#D26B37', light: '#DFC5C3', fg: '#ffffff' },
  NORTH_EAST: { bg: '#D26B37', light: '#DFC5C3', fg: '#ffffff' },
  CENTRAL: { bg: '#5C866C', light: '#DAE6DB', fg: '#ffffff' },
  EAST: { bg: '#0E96CD', light: '#CEDFEE', fg: '#ffffff' },
  WEST: { bg: '#902C8F', light: '#D5CCDA', fg: '#ffffff' },
  EC: { bg: '#E2AC00', light: '#FFE9A3', fg: '#1f2a44' },
  COMMERCIAL: { bg: '#F78D97', light: '#EBD5E0', fg: '#1f2a44' },
  INDUSTRIAL: { bg: '#F78D97', light: '#EBD5E0', fg: '#1f2a44' },
  COMMERCIAL_INDUSTRIAL: { bg: '#F78D97', light: '#EBD5E0', fg: '#1f2a44' },
};

export const PAGES: PageDef[] = [
  { key: 'cover', label: 'Cover', heading: null, accent: null, accentColor: '#f5d76e', sections: null },
  { key: 'overview', label: 'Overview (Tables)', heading: null, accent: null, accentColor: '#f5d76e', sections: null },
  {
    key: 'residential', label: 'Residential', heading: 'Residential', accent: null, accentColor: '#f5d76e',
    sections: ['NORTH', 'NORTH_EAST', 'CENTRAL', 'WEST', 'EAST', 'EC'],
  },
  {
    key: 'commercial-industrial', label: 'Commercial & Industrial', heading: 'Commercial & Industrial',
    accent: null, accentColor: '#f1948a', sections: ['COMMERCIAL', 'INDUSTRIAL', 'COMMERCIAL_INDUSTRIAL'],
  },
  {
    key: 'north-north-east', label: 'North & North-East', heading: 'Residential',
    accent: 'NORTH & NORTH—EAST', accentColor: '#e67e22', sections: ['NORTH', 'NORTH_EAST'],
  },
  {
    key: 'central', label: 'Central', heading: 'Residential',
    accent: 'CENTRAL', accentColor: '#7dba6f', sections: ['CENTRAL'],
  },
  {
    key: 'east', label: 'East', heading: 'Residential',
    accent: 'EAST', accentColor: '#2e9bd6', sections: ['EAST'],
  },
  {
    key: 'west', label: 'West', heading: 'Residential',
    accent: 'WEST', accentColor: '#c23d8f', sections: ['WEST'],
  },
  {
    key: 'ec', label: 'Executive Condo (EC)', heading: 'Executive Condo (EC)',
    accent: null, accentColor: '#f1c40f', sections: ['EC'],
  },
];

/** Continuous deck numbering: items grouped in section order, then sortOrder. */
export function numberItems(items: PipelineItem[]): Map<number, number> {
  const map = new Map<number, number>();
  let n = 1;
  for (const section of SECTION_ORDER) {
    items
      .filter((i) => i.section === section)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
      .forEach((i) => map.set(i.id, n++));
  }
  return map;
}
